import { api, isMockApi } from "@/lib/api";
import { ADMIN_SESSION_STORAGE_KEY, SESSION_STORAGE_KEY } from "@/data/authRepository";

function getStoredSession(storage, key) {
  try {
    return JSON.parse(storage.getItem(key) || "null");
  } catch {
    return null;
  }
}

function saveSession(storage, key, session) {
  storage.setItem(key, JSON.stringify(session));
}

function clearSession(storage, key) {
  storage.removeItem(key);
}

function toAuthError(error, fallback = "Authentication failed") {
  const normalised = new Error(error?.message || fallback);
  normalised.code = error?.code;
  return normalised;
}

function assertRole(payload, expectedRole) {
  const role = payload?.user?.role;
  const allowed = expectedRole === "admin" ? role === "super_admin" : role === "owner";
  if (!allowed) {
    throw toAuthError({ code: "ROLE_FORBIDDEN", message: "This account is not permitted to use this sign-in area." });
  }
}

function toVendorState(payload) {
  const restaurant = payload.restaurant || null;
  const workspace = payload.workspace || null;
  const user = payload.user;
  const role = "owner";
  const employee = null;
  // `/auth/me` includes the complete vendor workspace (storefronts,
  // categories, items, entitlements). Login/register only include the lean
  // restaurant object, so retain that as a safe fallback until hydration.
  const vendor = workspace
    ? { ...workspace, email: workspace.email || restaurant?.email || user.email }
    : restaurant ? { ...restaurant, email: restaurant.email || user.email } : null;

  return { vendor, employee, role, user, restaurant, principal: payload.principal || null };
}

function toPersistedSession(payload, previous = null) {
  return {
    ...previous,
    token: payload.token || previous?.token,
    expiresIn: payload.expiresIn || previous?.expiresIn,
    email: payload.user?.email || previous?.email,
    user: payload.user,
    restaurant: payload.restaurant || null,
    principal: payload.principal || null,
    workspace: payload.workspace || null,
  };
}

/**
 * Server-auth boundary. It is intentionally separate from the providers so it
 * can be tested without React and swapped independently from mock auth.
 */
export function createAuthSessionService({ apiClient = api, storage = window.localStorage } = {}) {
  // React StrictMode re-runs mount effects in development. Share an in-flight
  // restore so a remount does not duplicate the same `/auth/me` request.
  let vendorRestoreInFlight = null;
  let adminRestoreInFlight = null;

  async function loginVendor(credentials) {
    try {
      const payload = await apiClient.loginVendor(credentials);
      assertRole(payload, "vendor");
      if (!payload.token) throw toAuthError({ message: "The API did not return an access token." });
      saveSession(storage, SESSION_STORAGE_KEY, toPersistedSession(payload));
      return toVendorState(payload);
    } catch (error) {
      clearSession(storage, SESSION_STORAGE_KEY);
      throw toAuthError(error, "Unable to sign in");
    }
  }

  async function registerVendor(credentials) {
    try {
      const payload = await apiClient.registerVendor(credentials);
      assertRole(payload, "vendor");
      if (!payload.token) throw toAuthError({ message: "The API did not return an access token." });
      saveSession(storage, SESSION_STORAGE_KEY, toPersistedSession(payload));
      return toVendorState(payload);
    } catch (error) {
      clearSession(storage, SESSION_STORAGE_KEY);
      throw toAuthError(error, "Unable to register");
    }
  }

  async function restoreVendor() {
    if (vendorRestoreInFlight) return vendorRestoreInFlight;
    const previous = getStoredSession(storage, SESSION_STORAGE_KEY);
    if (!previous?.token) return null;
    vendorRestoreInFlight = (async () => {
      try {
        const payload = await apiClient.getCurrentVendor();
        assertRole(payload, "vendor");
        const merged = { ...payload, token: previous.token, expiresIn: previous.expiresIn };
        saveSession(storage, SESSION_STORAGE_KEY, toPersistedSession(merged, previous));
        return toVendorState(merged);
      } catch {
        clearSession(storage, SESSION_STORAGE_KEY);
        return null;
      } finally {
        vendorRestoreInFlight = null;
      }
    })();
    return vendorRestoreInFlight;
  }

  async function logoutVendor() {
    try {
      await apiClient.logoutVendor();
    } catch {
      // Stateless JWT logout cannot prevent the client from discarding a token.
    } finally {
      clearSession(storage, SESSION_STORAGE_KEY);
    }
  }

  async function loginAdmin(credentials) {
    try {
      const payload = await apiClient.loginAdmin(credentials);
      assertRole(payload, "admin");
      if (!payload.token) throw toAuthError({ message: "The API did not return an access token." });
      saveSession(storage, ADMIN_SESSION_STORAGE_KEY, toPersistedSession(payload));
      return payload.user;
    } catch (error) {
      clearSession(storage, ADMIN_SESSION_STORAGE_KEY);
      throw toAuthError(error, "Unable to sign in");
    }
  }

  async function restoreAdmin() {
    if (adminRestoreInFlight) return adminRestoreInFlight;
    const previous = getStoredSession(storage, ADMIN_SESSION_STORAGE_KEY);
    if (!previous?.token) return null;
    adminRestoreInFlight = (async () => {
      try {
        const payload = await apiClient.getCurrentAdmin();
        assertRole(payload, "admin");
        saveSession(storage, ADMIN_SESSION_STORAGE_KEY, toPersistedSession({ ...payload, token: previous.token }, previous));
        return payload.user;
      } catch {
        clearSession(storage, ADMIN_SESSION_STORAGE_KEY);
        return null;
      } finally {
        adminRestoreInFlight = null;
      }
    })();
    return adminRestoreInFlight;
  }

  async function logoutAdmin() {
    try {
      await apiClient.logoutAdmin();
    } catch {
      // See logoutVendor: local token removal is the reliable logout action.
    } finally {
      clearSession(storage, ADMIN_SESSION_STORAGE_KEY);
    }
  }

  return { loginVendor, registerVendor, restoreVendor, logoutVendor, loginAdmin, restoreAdmin, logoutAdmin };
}

export const serverAuth = isMockApi ? null : createAuthSessionService();
export { toAuthError, toVendorState };

// Re-exported so the providers subscribe from the session boundary they already
// import, rather than reaching into the HTTP adapter. Harmless under mock,
// where nothing ever dispatches it.
export { onSessionExpired } from "@/lib/api/adapters/http";
